import { useState, useCallback, useEffect, useContext, useRef } from 'react'
import AuthContext from '@/product/context/auth/authContext'
import UserContext from '@/product/context/user/userContext'
import { useHistory, useLocation as useRouterLocation } from 'react-router-dom'
import { handleRedirectInternal } from '../common/components'

const CACHE_KEY = 'search-location-header' // only used for last saved location

const allowedPaths = ['/', '/search'] // only redirect if on these paths (prevents redirect loops)

const DEFAULT_LOCATION = {
    address1: 'USA',
    city: '',
    state: '',
    zip: '',
    country: global.defaultCountry || 'US',
    full_address: '',
    place_id: 'ChIJCzYy51S161QRQrfeQ5K5Oxw',
    latitude: '39.8283',
    longitude: '-98.5795',
    radius: 1500,
}

const normalizeLocation = (loc) => ({
    ...DEFAULT_LOCATION,
    ...loc,
})

export const useLocation = () => {
    const history = useHistory()
    const routerLocation = useRouterLocation()

    const { user, isAuthenticated } = useContext(AuthContext)
    const { updateProfile } = useContext(UserContext)

    const [locationState, setLocationState] = useState(DEFAULT_LOCATION)

    const isDefaultLocation = (loc) =>
        loc.latitude === DEFAULT_LOCATION.latitude &&
        loc.longitude === DEFAULT_LOCATION.longitude &&
        loc.radius === DEFAULT_LOCATION.radius

    useEffect(() => {
        const init = () => {
            const cached = localStorage.getItem(CACHE_KEY)
            let finalLocation = cached ? normalizeLocation(JSON.parse(cached)) : DEFAULT_LOCATION

            // 2️⃣ Override with DB location if available
            if (isAuthenticated && user?.user_location) {
                finalLocation = normalizeLocation(JSON.parse(user.user_location))
            }

            // 3️⃣ Update state + cache
            setLocationState(finalLocation)
            localStorage.setItem(CACHE_KEY, JSON.stringify(finalLocation))

            if (
                !isDefaultLocation(finalLocation) &&
                allowedPaths.includes(routerLocation.pathname)
            ) {
                const params = new URLSearchParams({
                    longitude: finalLocation.longitude,
                    latitude: finalLocation.latitude,
                    radius: String(parseFloat(finalLocation.radius || 10) * 1609.34),
                }).toString()

                if (location.pathname !== '/search') {
                    handleRedirectInternal(history, `?${params}`)
                } else {
                    handleRedirectInternal(history, `search?${params}`)
                }
            }
        }

        init()
    }, [isAuthenticated, user])

    // single source setter: update state + localStorage
    const setLocationWithCache = useCallback((loc) => {
        const normalized = normalizeLocation(loc)
        setLocationState(normalized)
        localStorage.setItem(CACHE_KEY, JSON.stringify(normalized))
        return normalized
    }, [])

    // save location (UI + DB + redirect)
    const saveLocation = useCallback(
        async (values, onRedirect) => {
            const loc = setLocationWithCache(values)

            if (isAuthenticated && user?.id) {
                await updateProfile(
                    { id: user.id, user_location: JSON.stringify(loc) },
                    true,
                    'locationUpdate',
                )
            }

            if (typeof onRedirect === 'function') {
                const params = `longitude=${loc.longitude}&latitude=${loc.latitude}&radius=${
                    parseFloat(loc.radius || 10) * 1609.34
                }`
                onRedirect(params)
            }

            return loc
        },
        [isAuthenticated, user?.id, updateProfile, setLocationWithCache],
    )

    // reset to default
    const resetLocation = useCallback(() => {
        setLocationWithCache({ ...DEFAULT_LOCATION })
    }, [setLocationWithCache])

    function updateSearchUrlWithLocation({ params, history }) {
        const currentParams = new URLSearchParams(window.location.search)
        const newParams = new URLSearchParams(params)

        // ✅ get new location values
        const longitude = newParams.get('longitude')
        const latitude = newParams.get('latitude')
        const radius = newParams.get('radius')

        // ✅ final params (order controlled)
        const finalParams = new URLSearchParams()

        // 🔥 1. ADD LOCATION FIRST
        if (longitude) finalParams.set('longitude', longitude)
        if (latitude) finalParams.set('latitude', latitude)
        if (radius) finalParams.set('radius', radius)

        // 🔥 2. ADD REMAINING PARAMS
        currentParams.forEach((value, key) => {
            if (!['longitude', 'latitude', 'radius'].includes(key)) {
                finalParams.append(key, value)
            }
        })

        if (location.pathname !== '/search') {
            handleRedirectInternal(history, `?${params}`)
        } else {
            handleRedirectInternal(history, `search?${params}`)
        }

        // ✅ final query
        const finalQuery = finalParams.toString()

        // ✅ always force /search
        const finalUrl = finalQuery ? `/search?${finalQuery}` : '/'

        history.replace(finalUrl)

        return finalUrl
    }

    return {
        locationState,
        saveLocation,
        resetLocation,
        DEFAULT_LOCATION,
        updateSearchUrlWithLocation,
        isDefaultLocation,
    }
}
